import React from "react";
import { toast } from "sonner";
import {
  RectangleHorizontal, Circle, Triangle, Star, Hexagon, MousePointer2, Spline,
  Layers, Ruler, PenTool, RotateCcw, Move3d, Plus,
} from "lucide-react";
import { useCad } from "../context/CadContext";
import { SHAPE_DEFS, levelColor } from "../constants/defaults";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const SHAPE_ICONS = { rectangle: RectangleHorizontal, circle: Circle, triangle: Triangle, star: Star, polygon: Hexagon };

const Section = ({ title, icon: Icon, children, action }) => (
  <div className="px-3 py-3 border-b border-slate-800/80">
    <div className="flex items-center gap-2 mb-2.5">
      {Icon && <Icon size={13} className="text-emerald-400" />}
      <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">{title}</span>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, step = 0.1, min, testid, suffix = "mm" }) => (
  <label className="block">
    <span className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</span>
    <div className="relative mt-0.5">
      <input
        data-testid={testid}
        type="number"
        value={value}
        step={step}
        min={min}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full h-7 rounded-md bg-slate-950/60 border border-slate-700 px-2 pr-6 text-[11px] font-mono text-slate-100 focus:border-emerald-500 focus:outline-none"
      />
      {suffix && <span className="absolute right-1.5 top-1.5 text-[9px] text-slate-600 font-mono">{suffix}</span>}
    </div>
  </label>
);

export function LeftPanel() {
  const { addShape, addLevel, activeLevel, tool, setTool, selected, base, settings, setSettings, resetSettings, setBaseTransform } = useCad();
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const t = base?.transform || { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 };

  return (
    <aside className="w-64 lg:w-72 shrink-0 bg-[#0B1120] border-r border-slate-800 flex flex-col overflow-y-auto cad-scroll">
      <Section title="Add Shape" icon={Spline}>
        <div className="grid grid-cols-3 gap-2">
          {Object.keys(SHAPE_DEFS).map((type) => {
            const Icon = SHAPE_ICONS[type];
            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <button
                    data-testid={`add-${type}-btn`}
                    onClick={() => addShape(type)}
                    className="group aspect-square rounded-lg border border-slate-700/70 bg-slate-900/50 grid place-items-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-colors duration-150"
                  >
                    <Icon size={20} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-800 border-slate-700 text-slate-100 text-xs">{SHAPE_DEFS[type].label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Adds to</span>
          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded" style={{ background: levelColor(activeLevel) + "22", color: levelColor(activeLevel) }}>Level {activeLevel}</span>
          <button data-testid="new-level-btn" onClick={addLevel} className="ml-auto h-7 px-2 rounded-md bg-slate-900/60 border border-slate-700 text-[11px] text-slate-300 hover:bg-slate-800 flex items-center gap-1"><Plus size={12} /> New layer</button>
        </div>
        <p className="mt-1.5 text-[10px] text-slate-600 leading-relaxed">Multiple shapes can share a layer; select any shape to make its layer active.</p>
      </Section>

      <Section title="Tools" icon={MousePointer2}>
        <div className="grid grid-cols-2 gap-2">
          <ToolBtn testid="tool-select-btn" active={tool === "select"} onClick={() => setTool("select")} color="emerald" icon={MousePointer2} label="Select" />
          <ToolBtn testid="tool-connector-btn" active={tool === "connector"} onClick={() => setTool("connector")} color="sky" icon={Circle} label="Snap" />
          <ToolBtn testid="tool-draw-btn" active={tool === "draw"} onClick={() => setTool("draw")} color="emerald" icon={PenTool} label="Draw" />
          <ToolBtn testid="tool-measure-btn" active={tool === "measure"} onClick={() => setTool("measure")} color="amber" icon={Ruler} label="Measure" />
        </div>
        {tool === "connector" && (
          <p className="mt-2.5 text-[11px] text-sky-300/80 leading-relaxed">
            Click a shape to drop a snap point. Drag points with Select. Enable Edge snap to keep them {2}mm inside the outline.
          </p>
        )}
        {tool === "draw" && (
          <p className="mt-2.5 text-[11px] text-emerald-300/80 leading-relaxed">
            Click outline points on the canvas, then Finish (or double-click) to create a custom shape.
          </p>
        )}
        {tool === "measure" && (
          <p className="mt-2.5 text-[11px] text-amber-300/80 leading-relaxed">
            Click two points on the canvas to measure the distance in mm.
          </p>
        )}
      </Section>

      <Section
        title="Snap-Fit Defaults"
        icon={Ruler}
        action={
          <button
            data-testid="reset-defaults-btn"
            onClick={() => { resetSettings(); toast.success("Defaults restored"); }}
            className="ml-auto flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-emerald-400 transition-colors duration-150"
          >
            <RotateCcw size={11} /> Reset
          </button>
        }
      >
        <div className="mb-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">Peg shape</span>
          <div className="flex gap-1.5 mt-1">
            {["square", "diamond"].map((s) => (
              <button
                key={s}
                data-testid={`default-shape-${s}`}
                onClick={() => set("pegShape", s)}
                className={`flex-1 h-7 rounded-md text-[11px] capitalize transition-colors duration-150 ${
                  settings.pegShape === s ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-950/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >{s}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field testid="default-pegSize-input" label="Female" value={settings.pegSize} step={0.1} min={0.5} onChange={(v) => set("pegSize", v)} />
          <Field testid="default-tolerance-input" label="Tol/side" value={settings.tolerance} step={0.01} min={0} onChange={(v) => set("tolerance", v)} />
          <Field testid="default-maleHeight-input" label="Male H" value={settings.maleHeight} step={0.1} min={0.1} onChange={(v) => set("maleHeight", v)} />
          <Field testid="default-femaleExtra-input" label="Female +" value={settings.femaleExtra} step={0.05} min={0} onChange={(v) => set("femaleExtra", v)} />
          <Field testid="default-chamfer-input" label="Peg chamfer" value={settings.chamfer} step={0.01} min={0} onChange={(v) => set("chamfer", v)} />
          <Field testid="default-thickness-input" label="Thickness" value={settings.thickness} step={0.5} min={0.1} onChange={(v) => set("thickness", v)} />
        </div>
        <div className="mt-2.5 pt-2.5 border-t border-slate-800/70">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">Plate edge finish</span>
          <div className="flex gap-1.5 mt-1 mb-2">
            {["chamfer", "fillet"].map((s) => (
              <button
                key={s}
                data-testid={`default-edge-${s}`}
                onClick={() => set("edgeType", s)}
                className={`flex-1 h-7 rounded-md text-[11px] capitalize transition-colors duration-150 ${
                  settings.edgeType === s ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-950/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >{s}</button>
            ))}
          </div>
          <Field testid="default-edgeChamfer-input" label="Edge size" value={settings.edgeChamfer} step={0.1} min={0} onChange={(v) => set("edgeChamfer", v)} />
        </div>
        <div className="mt-2 text-[10px] font-mono text-slate-600 flex justify-between">
          <span>male <span className="text-sky-400">{(settings.pegSize - 2 * settings.tolerance).toFixed(2)}mm</span></span>
          <span>hole <span className="text-rose-400">{(settings.maleHeight + settings.femaleExtra).toFixed(2)}mm</span></span>
        </div>
      </Section>

      <Section title="Base Model" icon={Layers}>
        {base ? (
          <div className="text-xs space-y-2">
            <div className="text-slate-200 font-medium truncate" title={base.name}>{base.name}</div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500">Dimensions</span>
              <span className="font-mono text-emerald-400">{base.size.x.toFixed(1)}×{base.size.y.toFixed(1)}×{base.size.z.toFixed(1)}</span>
            </div>
            <div className="pt-1.5 border-t border-slate-800/70">
              <div className="flex items-center gap-1.5 mb-1.5 text-[10px] text-slate-500 uppercase tracking-wide">
                <Move3d size={11} /> Transform
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <Field testid="base-x-input" label="X" value={t.x} step={1} onChange={(v) => setBaseTransform({ x: v })} />
                <Field testid="base-y-input" label="Y" value={t.y} step={1} onChange={(v) => setBaseTransform({ y: v })} />
                <Field testid="base-z-input" label="Z" value={t.z} step={1} onChange={(v) => setBaseTransform({ z: v })} />
                <Field testid="base-rx-input" label="Rot X" value={t.rx} step={5} suffix="°" onChange={(v) => setBaseTransform({ rx: v })} />
                <Field testid="base-ry-input" label="Rot Y" value={t.ry} step={5} suffix="°" onChange={(v) => setBaseTransform({ ry: v })} />
                <Field testid="base-rz-input" label="Rot Z" value={t.rz} step={5} suffix="°" onChange={(v) => setBaseTransform({ rz: v })} />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-slate-500 leading-relaxed">
            No base imported. Use <span className="text-slate-300">Import Base</span> to load an STL/3MF model, then translate/rotate it here.
          </p>
        )}
      </Section>
    </aside>
  );
}

const COLOR = {
  emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40 shadow-[0_0_12px_rgba(16,185,129,0.15)]",
  sky: "bg-sky-500/15 text-sky-400 border-sky-500/40 shadow-[0_0_12px_rgba(56,189,248,0.15)]",
  amber: "bg-amber-500/15 text-amber-400 border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.15)]",
};

const ToolBtn = ({ testid, active, onClick, color, icon: Icon, label }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`h-10 rounded-lg border flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors duration-150 ${
      active ? COLOR[color] : "border-slate-700/70 bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800"
    }`}
  >
    <Icon size={14} /> {label}
  </button>
);
